/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Grid, Link, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "../../../common/DataTable";
import { ProcurementColDefs } from "./columnDefs";
import { showNotification } from "../../../common/Notification/ToastifyNotification";
import { apiRequest } from "../../../common/common";
import { LoadingPage } from "../../../common/LoadingPage";
import { useSelector } from "react-redux";
import ColumnPanel from "../../../common/ColumnPanel";
import ProcurementViewDialog from "./ProcurementViewDialog";

const Procurement = ({
    projectId,
    projectData
}) => {
    const { user } = useSelector((state) => state.users);
    const [rowsPerPage, setRowPerPage] = useState(10);
    const [gridApi, setGridApi] = useState(null);
    const [dataLoading, setDataLoading] = useState(false);
    const [itemColumn, setItemColumn] = useState(ProcurementColDefs);
    const [settingData, setSettingData] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [actionName, setActionName] = useState(null);
    const [actionId, setActionId] = useState(null);

    const onPaginationChanged = (event) => {
        if (event.api) {
            const newPageSize = event.api.paginationGetPageSize();
            setRowPerPage(newPageSize);
        }
    };

    const onGridReady = useCallback((params) => {
        if (params?.api) {
            const { api } = params;
            setGridApi(api);
            setDataLoading(true);
            const dataSource = {
                rowCount: null,
                getRows: async (params) => {
                    try {
                        const sortModel = params?.sortModel[0];
                        const sortBy = {
                            column: sortModel?.colId === 'purchase_order_status' ? 'po_status' :
                                sortModel?.colId === 'purchase_order_no' ? 'po_no' :
                                    sortModel?.colId === 'purchase_order_name' ? 'po_name' :
                                        sortModel?.colId || '',
                            order: sortModel?.sort || '',
                        };
                        const url = [
                            `/purchase-module/group-by-poid?projectId=${projectId}`,
                            `pageNo=${Math.floor((params.startRow / rowsPerPage) + 1)}`,
                            `pageSize=${rowsPerPage}`,
                            sortBy?.column ? `sortBy=${sortBy.column}` : '',
                            sortBy?.order ? `sortOrder=${sortBy.order}` : ''
                        ].filter(Boolean).join('&');
                        const response = await apiRequest('get', `${url}`);
                        const { data, total_count } = response.data;
                        let lastRow = -1;
                        if (data?.length <= total_count) {
                            lastRow = total_count;
                        }
                        params.successCallback(data, lastRow);
                        setTimeout(() => {
                            setDataLoading(false);
                        }, 3000);
                    } catch (error) {
                        setDataLoading(false);
                        showNotification(error?.response?.data.error || error.message, 'error');
                    }
                },
            };
            api.setGridOption("datasource", dataSource);
            setTimeout(() => {
                const node = api.getRenderedNodes();
                if (!node?.[0]?.displayed) {
                    api.showNoRowsOverlay();
                }
            }, 5000);
        }
    }, [rowsPerPage, projectId]);

    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            onGridReady({
                api: gridApi,
            });
        }
        return () => {
            isMounted = false;
        };
    }, [rowsPerPage, projectId]);

    const getData = async () => {
        try {
            setDataLoading(true)
            const response = await apiRequest('get', `/user-view?userId=${user?.user_id}&type=procurement&pageNo=1&pageSize=50`);
            const { data } = response.data;
            if (data?.length > 0) {
                if (data?.find((a) => a.is_pinned === true)?.columns) {
                    setItemColumn(data?.find((a) => a.is_pinned === true)?.columns);
                }
                setSettingData(data);
            } else {
                setSettingData([]);
            }
            setDataLoading(false);
        } catch (error) {
            setDataLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    useEffect(() => {
        if (user?.user_id) {
            getData();
        }
    }, [user?.user_id]);

    useEffect(() => {
        if (itemColumn?.length > 0 && gridApi) {
            const columnState = gridApi?.getColumnState()?.map(state => {
                const columnConfig = itemColumn.find((c) => c.field === state.colId || c.colId === state.colId);
                if (columnConfig) {
                    if (columnConfig.pinned !== state.pinned) {
                        state.pinned = columnConfig.pinned ? columnConfig.pinned : null;
                    }
                }
                return state;
            });
            gridApi?.applyColumnState({ state: columnState });
            gridApi?.refreshHeader();
        }
    }, [itemColumn]);

    const updatedColumns = useMemo(() => {
        let column = itemColumn;
        if (column?.length > 0) {
            return column?.map((col) => {
                switch (col.field) {
                    case 'pa_id':
                        return {
                            ...col,
                            sortable: true
                        };
                    case 'requisition_no':
                        return {
                            ...col,
                            sortable: true,
                            minWidth: 120,
                            flex: 1,
                            cellRenderer: (params) => {
                                if (params.data?.requisition_no) {
                                    return (
                                        <Link
                                            style={{ textDecoration: 'none', cursor: 'pointer' }}
                                            onClick={() => {
                                                setDialogOpen(true);
                                                setActionId(params?.data?.requisition_id);
                                                setActionName('requisition');
                                            }}
                                        >
                                            {params?.data?.requisition_no}
                                        </Link>
                                    )
                                } else {
                                    return 'NA'
                                }
                            },
                        };
                    case 'purchase_order_no':
                        return {
                            ...col,
                            sortable: true,
                            minWidth: 120,
                            flex: 1,
                            cellRenderer: (params) => {
                                if (params.data?.purchase_order_no) {
                                    return (
                                        <Link
                                            style={{ textDecoration: 'none', cursor: 'pointer' }}
                                            onClick={() => {
                                                setDialogOpen(true);
                                                setActionId(params?.data?.purchase_order_id);
                                                setActionName('purchase_action');
                                            }}
                                        >
                                            {params?.data?.purchase_order_no}
                                        </Link>
                                    )
                                } else {
                                    return 'NA'
                                }
                            },
                        };
                    case 'bidding_no':
                        return {
                            ...col,
                            sortable: true,
                            minWidth: 120,
                            flex: 1,
                            cellRenderer: (params) => {
                                if (params.data?.bidding_no) {
                                    return (
                                        <Link
                                            style={{ textDecoration: 'none', cursor: 'pointer' }}
                                            onClick={() => {
                                                setDialogOpen(true);
                                                setActionId(params?.data?.bidding_id);
                                                setActionName('bidding');
                                            }}
                                        >
                                            {params?.data?.bidding_no}
                                        </Link>
                                    )
                                } else {
                                    return 'NA'
                                }
                            },
                        };
                    case 'rfq_no':
                        return {
                            ...col,
                            sortable: true,
                            minWidth: 120,
                            flex: 1,
                            cellRenderer: (params) => {
                                if (params.data?.rfq_no) {
                                    return (
                                        <Link
                                            style={{ textDecoration: 'none', cursor: 'pointer' }}
                                            onClick={() => {
                                                setDialogOpen(true);
                                                setActionId(params?.data?.rfq_id);
                                                setActionName('rfq');
                                            }}
                                        >
                                            {params?.data?.rfq_no}
                                        </Link>
                                    )
                                } else {
                                    return 'NA'
                                }
                            },
                        };
                    case 'requisition_status':
                        return {
                            ...col,
                            sortable: true,
                            minWidth: 120,
                            flex: 1,
                            cellRenderer: (params) => {
                                if (params.data?.requisition_status) {
                                    return (
                                        <span>
                                            {`${params?.data?.requisition_status?.slice(0, 1)?.toUpperCase()}${params?.data?.requisition_status?.slice(1, params?.data?.requisition_status?.length)}`}
                                        </span>
                                    )
                                } else {
                                    return 'NA'
                                }
                            },
                        };
                    case 'bidding_status':
                        return {
                            ...col,
                            sortable: true,
                            cellRenderer: (params) => {
                                if (params.data?.bidding_status) {
                                    return (
                                        <span>
                                            {`${params?.data?.bidding_status?.slice(0, 1)?.toUpperCase()}${params?.data?.bidding_status?.slice(1, params?.data?.bidding_status?.length)}`}
                                        </span>
                                    )
                                } else {
                                    return 'NA'
                                }
                            },
                        };
                    case 'rfq_status':
                        return {
                            ...col,
                            sortable: true,
                            cellRenderer: (params) => {
                                if (params.data?.rfq_status) {
                                    return (
                                        <span>
                                            {`${params?.data?.rfq_status?.slice(0, 1)?.toUpperCase()}${params?.data?.rfq_status?.slice(1, params?.data?.rfq_status?.length)}`}
                                        </span>
                                    )
                                } else {
                                    return 'NA'
                                }
                            },
                        };
                    case 'purchase_order_status':
                        return {
                            ...col,
                            sortable: true,
                            cellRenderer: (params) => {
                                if (params.data?.purchase_order_status) {
                                    return (
                                        <span>
                                            {params?.data?.purchase_order_status === 'approved' ? 'Issued' : `${params?.data?.purchase_order_status?.slice(0, 1)?.toUpperCase()}${params?.data?.purchase_order_status?.slice(1, params?.data?.purchase_order_status?.length)}`}
                                        </span>
                                    )
                                } else {
                                    return 'NA'
                                }
                            },
                        };
                    case 'item_count':
                        return {
                            ...col,
                            sortable: false,
                            minWidth: 60,
                            flex: 1,
                            cellRenderer: (params) => {
                                if (params.data?.items.length > 0) {
                                    return (
                                        <span>
                                            {params?.data?.items.length}
                                        </span>
                                    )
                                } else {
                                    return 0
                                }
                            },
                        };
                    case 'requisition_name':
                        return {
                            ...col,
                            sortable: true,
                            cellRenderer: (params) => {
                                if (params.data?.requisition_name) {
                                    return (
                                        <span>
                                            {params?.data?.requisition_name}
                                        </span>
                                    )
                                } else {
                                    return '--'
                                }
                            },
                        };
                    case 'bidding_name':
                        return {
                            ...col,
                            sortable: true,
                            cellRenderer: (params) => {
                                if (params.data?.bidding_name) {
                                    return (
                                        <span>
                                            {params?.data?.bidding_name}
                                        </span>
                                    )
                                } else {
                                    return '--'
                                }
                            },
                        };
                    case 'rfq_name':
                        return {
                            ...col,
                            sortable: true,
                            cellRenderer: (params) => {
                                if (params.data?.rfq_name) {
                                    return (
                                        <span>
                                            {params?.data?.rfq_name}
                                        </span>
                                    )
                                } else {
                                    return '--'
                                }
                            },
                        };
                    case 'purchase_order_name':
                        return {
                            ...col,
                            sortable: true,
                            cellRenderer: (params) => {
                                if (params.data?.purchase_order_name) {
                                    return (
                                        <span>
                                            {params?.data?.purchase_order_name}
                                        </span>
                                    )
                                } else {
                                    return '--'
                                }
                            },
                        };
                    default:
                        return col;
                }
            });
        }
        return column;
    }, [itemColumn]);


    const toolPanel = useMemo(() => [
        {
            id: 'custom1',
            labelDefault: 'Columns',
            iconKey: "columns",
            toolPanel: ColumnPanel,
            width: 450,
            toolPanelParams: {
                columns: itemColumn,
                setColumns: (column) => {
                    setItemColumn(column);
                },
                settingData,
                fetchData: () => {
                    if (user?.user_id) {
                        getData();
                    }
                },
                type: "procurement",
                defaultVisibleColumns: ProcurementColDefs,
                columnOrder: itemColumn
            }
        }
    ], [gridApi, settingData, projectId, itemColumn]);


    return (
        <Grid container>
            {dataLoading && (<LoadingPage isLoading={dataLoading} />)}
            {dialogOpen && (
                <ProcurementViewDialog
                    dialogOpen={dialogOpen}
                    setDialogOpen={setDialogOpen}
                    projectData={projectData}
                    actionId={actionId}
                    actionName={actionName}
                    setActionId={setActionId}
                    setActionName={setActionName}
                    onGridReady={() => {
                        onGridReady({
                            api: gridApi
                        });
                    }}
                />
            )}
            <Grid item xs={12} sm={12} md={12} style={{ padding: '0px 8px 0px 8px' }}>
                <Box mt={1} ml={1}>
                    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} mb={1}>
                        <Typography variant="body1" style={{ fontWeight: 'bold' }}>Procurement</Typography>
                    </Box>
                </Box>
                <DataTable
                    height="63vh"
                    columnDefs={updatedColumns}
                    rowsPerPage={rowsPerPage}
                    paginationPageSizeSelector={[10, 15, 25, 50, 100]}
                    onPaginationChanged={onPaginationChanged}
                    modelType="infinite"
                    onGridReady={onGridReady}
                    toolPanel={toolPanel}
                />
            </Grid>
        </Grid>
    )
}

export default Procurement;